import { useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import toast from "react-hot-toast";
import { supabase } from "../../../supabase";

const RenameUserModal = ({ open, onClose, users, onSuccess }) => {
  const [selectedUserId, setSelectedUserId] = useState("");
  const [newName, setNewName] = useState("");
  const [newDbName, setNewDbName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleRenameUser = async () => {
    if (!selectedUserId) {
      toast.error("Please select a user");
      return;
    }
    if (!newName && !newDbName) {
      toast.error("Please enter at least one new name");
      return;
    }

    const selectedUser = users.find((u) => u.id === selectedUserId);
    if (!selectedUser) {
      toast.error("User not found");
      return;
    }

    const oldDbName = selectedUser.db_name;

    setLoading(true);

    try {
      // Update user table
      const updateData = {};
      if (newName) updateData.name = newName;
      if (newDbName) updateData.db_name = newDbName;

      const { error: userError } = await supabase
        .from("user")
        .update(updateData)
        .eq("id", selectedUserId);

      if (userError) throw userError;

      // Update assigned_member in related tables
      if (newDbName && oldDbName) {
        const { error: influencersError } = await supabase
          .from("influencers")
          .update({ assigned_member: newDbName })
          .eq("assigned_member", oldDbName);

        if (influencersError) throw influencersError;

        const { error: historyError } = await supabase
          .from("status_history")
          .update({ assigned_member: newDbName })
          .eq("assigned_member", oldDbName);

        if (historyError) console.warn("Status history update error:", historyError);
      }

      toast.success("User renamed successfully!");
      setSelectedUserId("");
      setNewName("");
      setNewDbName("");
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error renaming user:", error);
      toast.error("Failed to rename user: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Rename User
        <IconButton onClick={onClose} size="small">
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <FormControl fullWidth sx={{ mt: 1, mb: 2 }} size="small">
          <InputLabel>Select User</InputLabel>
          <Select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            label="Select User"
          >
            {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.name} ({user.db_name || "no db_name"})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="New Name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          size="small"
          sx={{ mb: 2 }}
          helperText="Leave empty to keep current name"
        />

        <TextField
          fullWidth
          label="New Database Name"
          value={newDbName}
          onChange={(e) => setNewDbName(e.target.value)}
          helperText="This will update assigned_member in all related tables"
          size="small"
        />
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 2 }}>
        <Button fullWidth variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button fullWidth variant="contained" disableElevation onClick={handleRenameUser} disabled={loading}>
          {loading ? "Renaming..." : "Rename User"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RenameUserModal;

